import type {
  DefaultContext,
  FetchResult,
  MutationResult,
} from '@apollo/client';

import { COOKIE_KEY_ACCESS } from '@constants/cookie-key';

import { useResponseHandler } from '@hooks/useResponseHandler';

import { tokenStorage } from '@utils/storage';

import type {
  AdminChangeEmailMutation,
  AdminChangeEmailMutationVariables,
  AdminChangePhoneMutation,
  AdminChangePhoneMutationVariables,
  ChangePasswordMutation,
  ChangePasswordMutationVariables,
  ChangeUserEmailMutation,
  ChangeUserEmailMutationVariables,
  ChangeUserPhoneMutation,
  ChangeUserPhoneMutationVariables,
  DisableMfaMutation,
  DisableMfaMutationVariables,
  DisableUserMutation,
  DisableUserMutationVariables,
  EnableMfaMutation,
  EnableMfaMutationVariables,
  EnableUserMutation,
  EnableUserMutationVariables,
  SendEmailVerificationCodeMutation,
  SendSmsMfaCodeMutation,
  SendSmsMfaCodeMutationVariables,
  SendTotpMfaCodeMutation,
  SetPreferredMfaMethodMutation,
  SetPreferredMfaMethodMutationVariables,
  VerifyUserEmailMutation,
  VerifyUserEmailMutationVariables,
  VerifyUserPhoneMutation,
  VerifyUserPhoneMutationVariables,
} from 'generated/session.generated';
import {
  useAdminChangeEmailMutation,
  useAdminChangePhoneMutation,
  useChangePasswordMutation,
  useChangeUserEmailMutation,
  useChangeUserPhoneMutation,
  useDisableMfaMutation,
  useDisableUserMutation,
  useEnableMfaMutation,
  useEnableUserMutation,
  useGetUserDisabledQuery,
  useSendEmailVerificationCodeMutation,
  useSendSmsMfaCodeMutation,
  useSendTotpMfaCodeMutation,
  useSetPreferredMfaMethodMutation,
  useVerifyUserEmailMutation,
  useVerifyUserPhoneMutation,
} from 'generated/session.generated';
import { GetUserMfaSettingsDocument } from 'generated/user.generated';

/**
 * Get request context for performing cognito-related operations
 * @description Cognito requires Access tokens to be used (does not work with ID tokens) for changing passwords, mfa settings, etc. so we must
 * override the default request context to use the access token
 * @returns Apollo request context
 */
const getCognitoUserReqContext = (): DefaultContext => {
  const accessToken = tokenStorage.getItem(COOKIE_KEY_ACCESS);
  return {
    headers: {
      authorization: accessToken,
    },
  };
};

export function useDisableUser(): [
  (
    params: DisableUserMutationVariables
  ) => Promise<FetchResult<DisableUserMutation>>,
  boolean,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'disableUser',
  });
  const [mutate, { loading: baseLoading }] = useDisableUserMutation();
  const loading = baseLoading;

  async function disableUser(params: DisableUserMutationVariables) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data }) => {
        if (!data?.disableUser) {
          return;
        }
        cache.modify({
          id: cache.identify({
            user_id: data.disableUser.user_id,
            __typename: 'UserSettings',
          }),
          fields: {
            disabled: () => data.disableUser.disabled,
          },
        });
      },
    });
    const { data, errors } = response;
    if (errors) {
      handleError(errors);
    }
    if (data?.disableUser && !errors) {
      handleSuccess();
    }
    return response;
  }
  return [disableUser, loading];
}

export function useEnableUser(): [
  (
    params: EnableUserMutationVariables
  ) => Promise<FetchResult<EnableUserMutation>>,
  boolean,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'enableUser',
  });
  const [mutate, { loading: baseLoading }] = useEnableUserMutation();
  const loading = baseLoading;

  async function enableUser(params: EnableUserMutationVariables) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data }) => {
        if (!data?.enableUser) {
          return;
        }
        cache.modify({
          id: cache.identify({
            user_id: data.enableUser.user_id,
            __typename: 'UserSettings',
          }),
          fields: {
            disabled: () => data.enableUser.disabled,
          },
        });
      },
    });
    const { data, errors } = response;
    if (errors) {
      handleError(errors);
    }
    if (data?.enableUser && !errors) {
      handleSuccess();
    }
    return response;
  }
  return [enableUser, loading];
}

export function useGetUserSettings(params: { user_id: string }) {
  const { handleError } = useResponseHandler();
  const response = useGetUserDisabledQuery({
    variables: { input: { user_id: params.user_id } },
    skip: !params.user_id,
  });
  handleError(response.error);
  return {
    ...response,
    data: response.data?.getUserSettings,
  };
}

export function useSendTotpMfaCode(): [
  () => Promise<FetchResult<SendTotpMfaCodeMutation>>,
  MutationResult<SendTotpMfaCodeMutation>,
] {
  const [mutate, response] = useSendTotpMfaCodeMutation();

  async function sendTotpCode() {
    const response = await mutate({
      context: getCognitoUserReqContext(),
    });
    return response;
  }
  return [sendTotpCode, response];
}

export function useSendSmsMfaCode(): [
  (
    params: SendSmsMfaCodeMutationVariables
  ) => Promise<FetchResult<SendSmsMfaCodeMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = useSendSmsMfaCodeMutation();
  const loading = baseLoading;

  async function sendSmsCode(params: SendSmsMfaCodeMutationVariables) {
    const response = await mutate({
      variables: { ...params },
      context: getCognitoUserReqContext(),
    });
    return response;
  }
  return [sendSmsCode, loading];
}

export function useEnableMfa(): [
  (
    params: EnableMfaMutationVariables
  ) => Promise<FetchResult<EnableMfaMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = useEnableMfaMutation();
  const loading = baseLoading;

  async function enableMfa(params: EnableMfaMutationVariables) {
    const response = await mutate({
      variables: { ...params },
      context: getCognitoUserReqContext(),
    });
    return response;
  }
  return [enableMfa, loading];
}

export function useDisableMfa(): [
  (
    params: DisableMfaMutationVariables
  ) => Promise<FetchResult<DisableMfaMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = useDisableMfaMutation();
  const loading = baseLoading;

  async function disableMfa(params: DisableMfaMutationVariables) {
    const response = await mutate({
      variables: { ...params },
      context: getCognitoUserReqContext(),
    });
    return response;
  }
  return [disableMfa, loading];
}

export function useSendEmailVerificationCode(): [
  () => Promise<FetchResult<SendEmailVerificationCodeMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] =
    useSendEmailVerificationCodeMutation();
  const loading = baseLoading;

  async function sendEmailCode() {
    const response = await mutate({
      context: getCognitoUserReqContext(),
    });
    return response;
  }
  return [sendEmailCode, loading];
}

export function useSetPreferredMfaMethod(): [
  (
    params: SetPreferredMfaMethodMutationVariables
  ) => Promise<FetchResult<SetPreferredMfaMethodMutation>>,
  boolean,
] {
  const [mutate, { loading: baseLoading }] = useSetPreferredMfaMethodMutation();
  const loading = baseLoading;

  async function setPreferredMethod(
    params: SetPreferredMfaMethodMutationVariables
  ) {
    const response = await mutate({
      variables: { ...params },
      context: getCognitoUserReqContext(),
      refetchQueries: [GetUserMfaSettingsDocument],
    });
    return response;
  }
  return [setPreferredMethod, loading];
}

export function useChangeUserPhone(): [
  (
    params: ChangeUserPhoneMutationVariables
  ) => Promise<FetchResult<ChangeUserPhoneMutation>>,
  MutationResult<ChangeUserPhoneMutation>,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'updatePhone',
  });
  const [mutate, response] = useChangeUserPhoneMutation();

  async function changePhone(params: ChangeUserPhoneMutationVariables) {
    const response = await mutate({
      variables: { ...params },
      context: getCognitoUserReqContext(),
    });
    const { data, errors } = response;
    if (errors) {
      handleError(errors);
    }
    if (data?.changeUserPhone && !errors) {
      handleSuccess();
    }
    return response;
  }
  return [changePhone, response];
}

export function useAdminChangePhone(): [
  (
    params: AdminChangePhoneMutationVariables
  ) => Promise<FetchResult<AdminChangePhoneMutation>>,
  MutationResult<AdminChangePhoneMutation>,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'updatePhone',
  });
  const [mutate, response] = useAdminChangePhoneMutation();

  async function adminChangePhone(params: AdminChangePhoneMutationVariables) {
    const response = await mutate({
      variables: { ...params },
    });
    const { data, errors } = response;
    if (errors) {
      handleError(errors);
    }
    if (data?.adminChangePhone && !errors) {
      handleSuccess();
    }
    return response;
  }
  return [adminChangePhone, response];
}

export function useVerifyPhone(): [
  (
    params: VerifyUserPhoneMutationVariables
  ) => Promise<FetchResult<VerifyUserPhoneMutation>>,
  boolean,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'verifyPhone',
  });
  const [mutate, { loading: baseLoading }] = useVerifyUserPhoneMutation();
  const loading = baseLoading;

  async function verifyPhone(params: VerifyUserPhoneMutationVariables) {
    const response = await mutate({
      variables: { ...params },
      context: getCognitoUserReqContext(),
    });
    const { data, errors } = response;
    if (errors) {
      handleError(errors);
    }
    if (data?.verifyUserPhone && !errors) {
      handleSuccess();
    }
    return response;
  }
  return [verifyPhone, loading];
}

export function useChangeUserEmail(): [
  (
    params: ChangeUserEmailMutationVariables
  ) => Promise<FetchResult<ChangeUserEmailMutation>>,
  MutationResult<ChangeUserEmailMutation>,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'updateEmail',
  });
  const [mutate, response] = useChangeUserEmailMutation();

  async function changeEmail(params: ChangeUserEmailMutationVariables) {
    const response = await mutate({
      variables: { ...params },
      context: getCognitoUserReqContext(),
    });
    const { data, errors } = response;
    if (errors) {
      handleError(errors);
    }
    if (data?.changeUserEmail && !errors) {
      handleSuccess();
    }
    return response;
  }
  return [changeEmail, response];
}

export function useAdminChangeEmail(): [
  (
    params: AdminChangeEmailMutationVariables
  ) => Promise<FetchResult<AdminChangeEmailMutation>>,
  MutationResult<AdminChangeEmailMutation>,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'updateEmail',
  });
  const [mutate, response] = useAdminChangeEmailMutation();

  async function adminChangeEmail(params: AdminChangeEmailMutationVariables) {
    const response = await mutate({
      variables: { ...params },
    });
    const { data, errors } = response;
    if (errors) {
      handleError(errors);
    }
    if (data?.adminChangeEmail && !errors) {
      handleSuccess();
    }
    return response;
  }
  return [adminChangeEmail, response];
}

export function useVerifyEmail(): [
  (
    params: VerifyUserEmailMutationVariables
  ) => Promise<FetchResult<VerifyUserEmailMutation>>,
  boolean,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'updateEmail',
  });
  const [mutate, { loading: baseLoading }] = useVerifyUserEmailMutation();
  const loading = baseLoading;

  async function verifyEmail(params: VerifyUserEmailMutationVariables) {
    const response = await mutate({
      variables: { ...params },
      context: getCognitoUserReqContext(),
    });
    const { data, errors } = response;
    if (errors) {
      handleError(errors);
    }
    if (data?.verifyUserEmail && !errors) {
      handleSuccess();
    }
    return response;
  }
  return [verifyEmail, loading];
}

export function useUpdatePassword(): [
  (
    params: ChangePasswordMutationVariables
  ) => Promise<FetchResult<ChangePasswordMutation>>,
  boolean,
] {
  const { handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'updatePassword',
  });
  const [mutate, { loading: baseLoading }] = useChangePasswordMutation();
  const loading = baseLoading;

  async function updatePassword(params: ChangePasswordMutationVariables) {
    const response = await mutate({
      variables: { ...params },
      context: getCognitoUserReqContext(),
    });
    const { data, errors } = response;
    // if (errors) {
    //   handleError(errors);
    // }
    if (data?.changePassword.message && !errors) {
      handleSuccess();
    }
    return response;
  }
  return [updatePassword, loading];
}
